import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import IndiaDataGrid from "../../include/IndiaDataGrid";
import { Box, Button, Container, MenuItem, TextField, Typography } from '@mui/material';
import { GridFooterContainer, GridPagination, GridToolbarColumnsButton, GridToolbarExportContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';

import { callPostApi } from '../../function/ApiCallFunction';
import { useNavigate } from 'react-router-dom';
import { DataContext } from '../../context/DataContext';
import { consoleColors, gridSx, primaryButtonSx } from '../admin/consoleTheme';
import { indiaDate, indiaDayKey } from '../../include/indiaDate';

const ApiList = () => {

    const [allData,        setAllData]        = useState([]);
    const [loading,        setLoading]        = useState(false);
    const [pageIndex,      setPageIndex]      = useState(0);
    const [hasMoreData,    setHasMoreData]    = useState(true);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [filtersLoaded,  setFiltersLoaded]  = useState(false);
    
    const isInitialSessionLoad = useRef(false);
    const hasFetchedRef        = useRef(false);
    const navigate = useNavigate();

    const {loginData }  = useContext(DataContext);
    const store_id      = loginData?.store_info?.store_id;

    /* ------------------ DATE HELPER ------------------ */
    const getDateNDaysAgo = (n) => {
        const date = new Date();
        date.setDate(date.getDate() - n);
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        return `${year}-${month}-${day}`;
    };
      
    // Default Filter For Searching
    const defaultFilters = useMemo(() => ({store_id, start_date: getDateNDaysAgo(0), end_date: getDateNDaysAgo(0), fetchLimit: '10', pageSize: '10' }), [store_id]);
    const [filters, setFilters] = useState(defaultFilters);

    // Reset Data States
    const resetDataStates = () => 
    {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        setLoading(false);
    };

    // Apply Filters
    const applyFilters = () => 
    {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        setFiltersApplied(true);
    };

    // Clear Filters
    const clearFilters = () => 
    {
        setFilters(defaultFilters);
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        sessionStorage.setItem('luckyDrawFilfer', JSON.stringify(defaultFilters));
        setFiltersApplied(true);
    };

    // Load from sessionStorage
    useEffect(() => 
    {
        const storedState = sessionStorage.getItem('luckyDrawFilfer');
        if (storedState) 
        {
            try 
            {
                const parsedState = JSON.parse(storedState);
                setFilters({ ...defaultFilters, ...parsedState });
                isInitialSessionLoad.current = true;
                // setFiltersApplied(true);
            } 
            catch (error) 
            {
               
                sessionStorage.removeItem('luckyDrawFilfer');
            }
        }
    }, [defaultFilters]);

    // Fetch Data Function
    const fetchData = useCallback(async (page = pageIndex) => 
    {
        if (!filtersLoaded || loading || !hasMoreData) return;

        setLoading(true);
        try 
        {
            const StartNum = page * filters.fetchLimit;
            const payload = {mod: "CASHI_LUCKY_REDEEM_LIST", data_arr: {...filters, draw_date: filters.start_date, start: StartNum, limit: filters.fetchLimit,}};

            const response = await callPostApi('store-cashi-report', payload);

            if (response.status === '200' && response.data) 
            {
                const pack = Array.isArray(response.data) ? { entries: response.data } : response.data;
                const apiResponse = pack.entries || [];
                if (apiResponse.length > 0) 
                {
                    setAllData(page === 0 ? apiResponse : (prev) => [...prev, ...apiResponse]);
                    setPageIndex(page);
                } 
                else { setAllData(page === 0 ? [] : (prev) => prev); }
                setHasMoreData(false);
            } 
            else 
            {
                setHasMoreData(false);
            }
        } 
        catch (error) { setHasMoreData(false); } 
        finally { setLoading(false); }

    }, [filters, filtersLoaded, loading, hasMoreData, pageIndex]);

    // Initial fetch trigger
    
     useEffect(() => { if (!filtersLoaded) setFiltersLoaded(true); }, [filtersLoaded]);

    // Save to sessionStorage
    useEffect(() => 
    {
         if (!hasFetchedRef.current && filtersLoaded) {
            hasFetchedRef.current = true;
            fetchData();
        }
  
        if (filtersApplied) 
        {
            sessionStorage.setItem('luckyDrawFilfer', JSON.stringify(filters));
            if (!isInitialSessionLoad.current) { fetchData(0); }
            isInitialSessionLoad.current = false;
            setFiltersApplied(false);
        }

        const handleExternalRefresh = () => 
        {
            resetDataStates();
            hasFetchedRef.current = false;
            fetchData(0);
        };
        window.addEventListener('refreshPriceTable', handleExternalRefresh);
        return () => window.removeEventListener('refreshPriceTable', handleExternalRefresh);

    }, [filtersLoaded, filtersApplied, filters,fetchData]);
    
    const CustomToolbar = () => {
        return (
            <GridToolbarExportContainer sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                <GridToolbarColumnsButton /><GridToolbarQuickFilter debounceMs={300} />
            </GridToolbarExportContainer>
        );
    };

    const CustomFooterWithLoadMore = ({ hasMoreData, loading, onLoadMore }) => (
      <GridFooterContainer sx={{ px: 2, py: 1, overflowX: 'hidden', }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', width: '100%', }} >
          
              {/* Left: Rows per page */}
              <Box sx={{ flex: 1 }} />
              {/* Center: Load More */}
              
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  {hasMoreData ? (
                      <Button variant="contained" onClick={onLoadMore} disabled={loading} sx={{ minWidth: 120 }}>
                          {loading ? 'Loading...' : 'Load More'}
                      </Button>
                  ) : (<Typography variant="body2" color="text.secondary">No more records </Typography>)}
              </Box>

              {/* Right: Pagination - Increased width */}
              <Box sx={{ flex: 2, minWidth: 250, display: 'flex', justifyContent: 'flex-end' }}>
                  <GridPagination />
              </Box>
              
          </Box>
      </GridFooterContainer>
    );

    const fetchLimits    = [ "5", "10", "20", "50", "100", "250", "500", "1000"];

    // Download the data as an Excel file
    const handleDownloadExcel = () => 
    {
        if (allData.length === 0) 
        {
            alert("No data available to export.");
            return;
        }
        
        // Rename headers in the Excel file
        const renamedHeaders = allData.length > 0 ? allData.map((item, index) => (
        {
            'Sr. No': index + 1,  // Index starts from 0, so we add 1 for "Sr. No"
            'Name'          : item.customer_name,
            'Mobile'        : item.customer_mobile,
            'Email'         : item.customer_email,
            'Order Amount'  : item.order_amount,
            'Draw Code'     : item.coupon_code,
            'Store'         : item.store_name,
            'Date Added'    : indiaDate(item.date_added),
            
        })) : [];
        
        const filename = `luckyDrawReport_${indiaDayKey()}.xlsx`;
        const worksheet = XLSX.utils.json_to_sheet(renamedHeaders);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Lucky Draw Coupons');
        XLSX.writeFile(workbook, filename);

    };

    return (
    <div>        
    <Container>

        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate(-1)}>Back</Button>
            <Button variant="contained" sx={{ ...primaryButtonSx, ml: "auto" }} onClick={handleDownloadExcel}>Download Excel</Button>
        </Box>

        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <TextField size="small" type="date" label="From" value={filters.start_date} InputLabelProps={{ shrink: true }}
                    onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))} sx={{ minWidth: 160 }} />
                <TextField size="small" type="date" label="To" value={filters.end_date} InputLabelProps={{ shrink: true }}
                    onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))} sx={{ minWidth: 160 }} />
                <TextField select size="small" label="Rows" value={filters.fetchLimit}
                    onChange={(e) => setFilters((prev) => ({ ...prev, fetchLimit: e.target.value }))} sx={{ minWidth: 120 }}>
                    {fetchLimits.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                </TextField>
                <Button variant="contained" sx={primaryButtonSx} onClick={() => { resetDataStates(); applyFilters(); }}>Apply</Button>
                <Button variant="contained" sx={primaryButtonSx} onClick={clearFilters}>Clear</Button>
            </Box>
        </Box>

        <Box sx={{ height: "auto", width: "100%", background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
            <IndiaDataGrid rows={allData.map((row, i) => ({...row,srNo: i + 1 ,id: row.row_temp_id || `${pageIndex}-${i}`}))}
                columns={[
                    { field: 'srNo',            headerName: 'Sr. No.',      flex: 0.5, sortable: false, filterable: false,},
                    { field: 'customer_name',   headerName: 'Name',         flex: 1, },
                    { field: 'customer_mobile', headerName: 'Mobile',       flex: 1, },
                    { field: 'customer_email',  headerName: 'Email',        flex: 1, },
                    { field: 'order_amount',    headerName: 'Order Amount', flex: 1, },
                    { field: 'coupon_code',     headerName: 'Draw Code',    flex: 1, },
                    { field: 'store_name',      headerName: 'Store',        flex: 1, },
                    { field: 'date_added',      headerName: 'Date Added',   flex: 1, },
                    { field: 'is_winner',       headerName: 'Winner',       width: 90,
                        renderCell: (params) => params.row.is_winner === "1" ? "Winner" : "", },                    
                  ]}
                
                localeText={{ noRowsLabel: "No records found", noResultsOverlayLabel: "No matching data found", }}
                
                pageSize={Number(filters.pageSize)}
                onPageSizeChange={(newSize) => setFilters((prev) => ({ ...prev, pageSize: String(newSize) })) }
                pageSizeOptions={[10, 15, 20, 25, 40, 50, 80, 100]}
                pagination
                
                disableSelectionOnClick
                initialState={{pagination: {paginationModel: {pageSize: Number(filters.pageSize),page: 0,},},}}
                slots={{toolbar: CustomToolbar, footer: () => ( <CustomFooterWithLoadMore onLoadMore={() => fetchData(pageIndex + 1)} hasMoreData={hasMoreData} loading={loading} /> ), }}
                slotProps={{toolbar: {showQuickFilter: true,quickFilterProps: { debounceMs: 300 },},}}
                sx={gridSx}
                rowHeight={38} headerHeight={45}
                
            />
        </Box>
    
    </Container>
    </div>
  );
};

export default ApiList;